// Friendly display name for a peer (DM counterpart, contact, etc).
//
// A Tizok handle looks like "@bob:tizok.dev". When a peer has no explicit
// display name, showing that raw handle in the UI reads like a machine id.
// Familiar chat apps (Slack/Discord/WhatsApp) show a friendly name and keep
// the full handle available on hover — this mirrors that: prefer the display
// name, else derive the handle's local-part ("@bob:tizok.dev" → "bob").

/**
 * Extract the readable local-part of a handle.
 *
 *   "@bob:tizok.dev" → "bob"
 *   "bob:tizok.dev"  → "bob"
 *   "@bob"           → "bob"
 *   "bob"            → "bob"
 *
 * If the handle has no derivable local-part (empty after trimming the "@"),
 * the original handle is returned so nothing renders blank.
 */
export function handleLocalPart(handle: string): string {
  const trimmed = handle.trim();
  const withoutAt = trimmed.startsWith('@') ? trimmed.slice(1) : trimmed;
  const colonIdx = withoutAt.indexOf(':');
  const local = colonIdx === -1 ? withoutAt : withoutAt.slice(0, colonIdx);
  return local.length > 0 ? local : trimmed;
}

/**
 * Friendly name for a peer: prefer an explicit display name, otherwise fall
 * back to the handle's local-part. The full handle stays available to the
 * caller (e.g. as a `title`/tooltip) so the exact address is never lost.
 */
export function friendlyPeerName(
  displayName: string | null | undefined,
  handle: string | null | undefined,
): string {
  const trimmedDisplay = displayName?.trim();
  if (trimmedDisplay) return trimmedDisplay;
  return handleLocalPart(handle ?? '');
}
